// A small markdown renderer for chat answers — the subset a chat model
// actually emits: fenced code, headings, ordered/bulleted lists, blockquotes,
// paragraphs, and inline code / bold / italic / links.
//
// Hand-rolled rather than pulled from npm for the same reason the rest of this
// app is: no runtime dependency to audit, and the output is React elements, so
// nothing is ever injected as raw HTML. Anything it doesn't recognise renders
// as plain text, which is the safe direction to fail.
//
// It is fed partial text while a message streams, so it must tolerate an
// unterminated fence or a half-written list without throwing — an open fence is
// simply treated as running to the end of what has arrived so far.
import { useMemo, useState } from 'react'
import { CopyIcon, CheckIcon } from './icons.jsx'

// ── inline ─────────────────────────────────────────────────────────────────
// One pass, longest-delimiter-first so ** wins over *.
const INLINE = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(\[[^\]\n]+\]\([^)\s]+\))/g

function renderInline(text, keyPrefix) {
  const out = []
  let last = 0
  let match
  let i = 0
  INLINE.lastIndex = 0
  while ((match = INLINE.exec(text))) {
    if (match.index > last) out.push(text.slice(last, match.index))
    const token = match[0]
    const key = `${keyPrefix}-i${i++}`
    if (token.startsWith('`')) {
      out.push(
        <code
          key={key}
          className="rounded-[5px] bg-panel px-[5px] py-[2px] font-mono text-[0.9em] text-ink [border:1px_solid_var(--border)]"
        >
          {token.slice(1, -1)}
        </code>,
      )
    } else if (token.startsWith('**')) {
      out.push(
        <strong key={key} className="font-semibold text-ink">
          {token.slice(2, -2)}
        </strong>,
      )
    } else if (token.startsWith('*')) {
      out.push(<em key={key}>{token.slice(1, -1)}</em>)
    } else {
      const split = token.indexOf('](')
      const label = token.slice(1, split)
      const href = token.slice(split + 2, -1)
      // Only http(s) and in-app paths: a javascript: or data: URL in model
      // output should render as text, never as a live link.
      //
      // AN IN-APP PATH IS ONE SLASH, NOT TWO. `//evil.com` is a PROTOCOL-
      // RELATIVE URL: it starts with "/" and so read as internal, but it
      // navigates off-site — pushState throws on it (cross-origin) and a
      // cmd-click would follow the raw href straight to evil.com. `/\evil.com`
      // is the same trick, since browsers normalise the backslash. Model
      // output is influenced by third-party CRM text, so this is a live
      // phishing vector, not a hypothetical: such a link renders as TEXT.
      const internal = /^\/(?![/\\])/.test(href)
      const safe = internal || /^https?:\/\//i.test(href)
      out.push(
        safe ? (
          <a
            key={key}
            href={href}
            target={internal ? undefined : '_blank'}
            rel="noreferrer noopener"
            // An in-app path (the model links a lead page as
            // /leads/company/<key>) stays inside the SPA: pushState + a
            // popstate event is what BrowserRouter listens for, so this
            // navigates without a full reload — and without this file taking a
            // Router dependency (it renders in surfaces outside one too).
            onClick={
              internal
                ? (e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                    e.preventDefault()
                    window.history.pushState({}, '', href)
                    window.dispatchEvent(new PopStateEvent('popstate'))
                  }
                : undefined
            }
            className="text-primary-dark underline underline-offset-2"
          >
            {label}
          </a>
        ) : (
          <span key={key}>{token}</span>
        ),
      )
    }
    last = match.index + token.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

// ── code block ─────────────────────────────────────────────────────────────
function CodeBlock({ code, lang }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="my-3 overflow-hidden rounded-[10px] bg-panel [border:1px_solid_var(--border)]">
      <div className="flex items-center justify-between px-3 py-1.5 [border-bottom:1px_solid_var(--border)]">
        <span className="font-mono text-[11.5px] text-faint">{lang || 'code'}</span>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(code)
            setCopied(true)
            setTimeout(() => setCopied(false), 1600)
          }}
          className="inline-flex cursor-pointer items-center gap-x-1.5 rounded-[6px] bg-transparent px-1.5 py-1 text-[11.5px] font-medium text-faint transition-colors hover:text-ink"
        >
          {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto px-3 py-2.5">
        <code className="font-mono text-[12.5px] leading-[1.6] text-ink">{code}</code>
      </pre>
    </div>
  )
}

// ── block parsing ──────────────────────────────────────────────────────────
function parseBlocks(src) {
  const lines = src.split('\n')
  const blocks = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // fenced code — an unterminated fence runs to the end (streaming)
    const fence = line.match(/^```(\w*)\s*$/)
    if (fence) {
      const lang = fence[1]
      const body = []
      i++
      while (i < lines.length && !/^```\s*$/.test(lines[i])) body.push(lines[i++])
      if (i < lines.length) i++ // closing fence
      blocks.push({ type: 'code', lang, code: body.join('\n') })
      continue
    }

    if (!line.trim()) {
      i++
      continue
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/)
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] })
      i++
      continue
    }

    if (/^\s*([-*+])\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*([-*+])\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*+])\s+/, ''))
        i++
      }
      blocks.push({ type: 'ul', items })
      continue
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        // Strip the list marker, keeping the number so a duplicate can be
        // recognised below.
        const marker = lines[i].match(/^\s*(\d+)[.)]\s+/)
        items.push({ n: marker[1], text: lines[i].replace(/^\s*\d+[.)]\s+/, '') })
        i++
      }
      // Some models number the item text as well ("1. 1.Chicago"), which
      // would render twice since this component draws its own numbers. Strip
      // the repeat ONLY when it matches the item's own number — a conservative
      // rule, so a line that legitimately starts with a figure ("1. 2024. was
      // a strong year") keeps it.
      blocks.push({
        type: 'ol',
        items: items.map((it) =>
          it.text.replace(new RegExp(`^\\s*${it.n}[.)]\\s*`), ''),
        ),
      })
      continue
    }

    if (/^\s*>\s?/.test(line)) {
      const body = []
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*>\s?/, ''))
        i++
      }
      blocks.push({ type: 'quote', text: body.join(' ') })
      continue
    }

    // paragraph — runs until a blank line or the start of another block
    const para = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^```/.test(lines[i]) &&
      !/^(#{1,4})\s+/.test(lines[i]) &&
      !/^\s*([-*+])\s+/.test(lines[i]) &&
      !/^\s*\d+[.)]\s+/.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i])
    ) {
      para.push(lines[i])
      i++
    }
    blocks.push({ type: 'p', text: para.join(' ') })
  }
  return blocks
}

const HEADING_CLASS = {
  1: 'mt-4 mb-2 text-[17px] font-bold tracking-[-0.01em] text-ink first:mt-0',
  2: 'mt-4 mb-2 text-[15.5px] font-bold tracking-[-0.01em] text-ink first:mt-0',
  3: 'mt-3.5 mb-1.5 text-[14.5px] font-semibold text-ink first:mt-0',
  4: 'mt-3 mb-1.5 text-[14px] font-semibold text-ink first:mt-0',
}

export default function Markdown({ text }) {
  const blocks = useMemo(() => parseBlocks(text || ''), [text])

  return (
    <div className="text-[14.5px] leading-[1.65] text-dim">
      {blocks.map((b, n) => {
        const key = `b${n}`
        if (b.type === 'code') return <CodeBlock key={key} code={b.code} lang={b.lang} />
        if (b.type === 'heading') {
          const Tag = `h${Math.min(b.level + 1, 6)}`
          return (
            <Tag key={key} className={HEADING_CLASS[b.level] || HEADING_CLASS[4]}>
              {renderInline(b.text, key)}
            </Tag>
          )
        }
        if (b.type === 'ul') {
          return (
            <ul key={key} className="my-2 flex list-none flex-col gap-y-1.5 pl-1">
              {b.items.map((item, k) => (
                <li key={`${key}-${k}`} className="flex gap-x-2.5">
                  <span aria-hidden className="mt-[9px] h-[4px] w-[4px] shrink-0 rounded-full bg-faint" />
                  <span>{renderInline(item, `${key}-${k}`)}</span>
                </li>
              ))}
            </ul>
          )
        }
        if (b.type === 'ol') {
          return (
            <ol key={key} className="my-2 flex list-none flex-col gap-y-1.5 pl-1">
              {b.items.map((item, k) => (
                <li key={`${key}-${k}`} className="flex gap-x-2.5">
                  <span aria-hidden className="mt-[1px] w-[15px] shrink-0 text-[13px] font-semibold tabular-nums text-faint">
                    {k + 1}.
                  </span>
                  <span>{renderInline(item, `${key}-${k}`)}</span>
                </li>
              ))}
            </ol>
          )
        }
        if (b.type === 'quote') {
          return (
            <blockquote
              key={key}
              className="my-2.5 pl-3 text-dim [border-left:2px_solid_var(--border)]"
            >
              {renderInline(b.text, key)}
            </blockquote>
          )
        }
        return (
          <p key={key} className="my-2 first:mt-0 last:mb-0">
            {renderInline(b.text, key)}
          </p>
        )
      })}
    </div>
  )
}
